import memoize from 'memoize-one';
import { difference, has, isEmpty, isNil, path, pathOr, pick, propEq, propOr } from '../utils/fp';
import { availabilityQueryUrl, availabilityQueryUrlV1 } from '@dbmx/sdmx';
import Set from 'es6-set';
import * as rules from '../lib/rules';
import { applyConstraintsToDimensions } from '../lib/sdmx/computors/dimensions';
import {
  getFrequencies,
  parseDateFromSdmxPeriod,
  getFrequencyFromSdmxPeriodSelection,
  getAjustedDate,
} from '../lib/sdmx/frequency';
import { getDateInTheRange } from '../utils/date';
import {
  dataRequestSize,
  getDatasource as getSettingsDatasource,
  sdmxPeriodBoundaries,
  getSpaceFromUrl,
  getSpaceRangeSizeLimit as getSettingsRangeSizeLimit,
  isPaginationActivated,
} from '../lib/settings';
import {
  getLastNObservations,
  getLocale,
  getPeriod as getRouterPeriod,
  getHasDataAvailability,
  getDataquery,
  getDataflow,
  getDisplay,
  getLastNMode,
} from './router';
import {
  dataFileRequestArgsWrapper,
  dataRequestArgsWrapper,
  getDataQueryUrl,
  getDataRequestParams as lib_getDataRequestParams,
  getOnlyHasDataDimensions,
  getStructureQueryUrl as lib_getStructureQueryUrl,
  rawDataRequestArgsWrapper,
} from '../lib/sdmx';
import { handleDataVersion } from '../lib/sdmx/version';
import { applyAutomatedSelection } from '../lib/sdmx/selection';
import { cleanDeadBranches } from '../utils/clean-dead-branches';
import { getMultiHierarchicalFilters } from '../lib/sdmx/hierarchical-codelist';
import {
  getTimePeriodArtefact as _getTPArtefact,
  getTimePeriodContraints,
  getFrequencyArtefact as LIB_getFrequencyArtefact,
} from '../lib/sdmx/structure';
import { getStructureHeaderFormat } from '../lib/headers';
import { getIsV3 } from '../lib/utils';
import { rangeTimeSelection } from '../lib/sdmx/metadata';

export const getSdmx = state => state?.sdmx;

const getMicrodataDimensionId = memoize(state => path(['microdata', 'dimensionId'], state));

export const getAnnotations = memoize(state =>
  pathOr([], ['data', 'structure', 'annotations'], getSdmx(state)),
);

const _getDimensions = memoize(state => propOr([], 'dimensions', getSdmx(state)));
export const getAttributes = memoize(state => propOr([], 'attributes', getSdmx(state)));
export const getPage = memoize(state => propOr(0, 'page', getSdmx(state)));
export const getHiddenValuesAnnotation = memoize(state => getSdmx(state)?.hiddenValuesAnnotation);
export const getData = memoize(state => getSdmx(state)?.data);
export const getExternalReference = memoize(state => getSdmx(state)?.externalReference);
export const getConstraints = memoize(state => getSdmx(state)?.constraints);

const getTargetDataflow = memoize(state => {
  const dataflow = getDataflow(state);
  const externalReference = getExternalReference(state);
  return {
    agencyID: externalReference?.identifiers?.agencyId || dataflow?.agencyId,
    id: externalReference?.identifiers?.code || dataflow?.dataflowId,
    version: externalReference?.identifiers?.version || dataflow?.version,
  };
});

export const getDataflowName = memoize(state => {
  const sdmx = getSdmx(state);
  return isNil(sdmx.data) ? sdmx.name : path(['structure', 'name'], sdmx.data);
});

export const getHierarchies = memoize(state => getSdmx(state)?.hierarchies);
export const getHCodes = memoize(state => getSdmx(state)?.hCodes);
export const getCombinationsDefinition = memoize(state =>
  propOr([], 'combinationsDefinition', getSdmx(state)),
);
export const getParsedAttributes = memoize(state => propOr([], 'parsedAttributes', getSdmx(state)));
export const getActualContentConstraints = memoize(state =>
  propOr([], 'actualContentConstraints', getSdmx(state)),
);
export const getActuallContentConstraintId = memoize(state =>
  propOr(null, 'constraintId', getSdmx(state)),
);
const getTimePeriod = memoize(state => propOr(null, 'timePeriod', getSdmx(state)));
const getCustomDataRequestSize = memoize(state => getSdmx(state)?.customDataRequestSize);

export const getActualContentConstraint = memoize(state => {
  const constraints = getActualContentConstraints(state);
  const constraintId = getActuallContentConstraintId(state);
  // propEq, not constraint.id: constraintId is undefined when the dataflow has
  // no live constraint, and the lookup stays tolerant of a nil item.
  return constraints.find(propEq(constraintId, 'id')) ?? {};
});

export const getContentConstraintAttributes = memoize(state =>
  propOr({}, 'constraintAttributes', getSdmx(state)),
);

export const getTimePeriodArtefact = memoize(state => {
  const constraint = getActualContentConstraint(state);
  const timePeriod = getTimePeriod(state);
  const isTimePeriodDisable = isNil(timePeriod) || !timePeriod?.display;

  const timePeriodConstraint = getTimePeriodContraints(
    isTimePeriodDisable,
    timePeriod,
    propOr({}, 'cubeRegions', constraint),
  );

  return _getTPArtefact({
    timePeriod,
    isTimePeriodDisable,
    timePeriodBoundaries: propOr([], 'boundaries', timePeriodConstraint),
  });
});

export const getDimensions = memoize(state => {
  const dimensions = _getDimensions(state);
  const constraint = getActualContentConstraint(state);
  return applyConstraintsToDimensions(dimensions, constraint?.cubeRegions || {});
});

export const getAvailableTimePeriod = memoize(state => getConstraints(state)?.availableTimePeriod);

export const getFrequencyArtefact = memoize(state => {
  const dimensions = getDimensions(state);
  const attributes = getAttributes(state);
  return LIB_getFrequencyArtefact({ dimensions, attributes });
});

export const getDatasource = memoize(state => {
  const dataflow = getDataflow(state);
  const externalReference = getExternalReference(state);
  if (!isNil(externalReference)) {
    const configuredSpace = getSpaceFromUrl(externalReference.datasource.url);
    return configuredSpace || externalReference?.datasource;
  }
  return getSettingsDatasource(dataflow?.datasourceId) ?? {};
});

export const getRawStructureRequestArgs = memoize(state => {
  const dataflow = getDataflow(state);
  const locale = getLocale(state);
  const externalReference = getExternalReference(state);

  if (!isNil(externalReference)) return { ...externalReference, locale };

  return {
    datasource: getSettingsDatasource(dataflow?.datasourceId),
    identifiers: {
      code: dataflow?.dataflowId,
      ...pick(['agencyId', 'version'], dataflow),
    },
    locale,
  };
});

const getDataRequestSize = memoize(state => {
  return isPaginationActivated
    ? dataRequestSize
    : getCustomDataRequestSize(state) || dataRequestSize;
});

// The number of cells the table is truncated at (rules2 refineLayoutSize), 0 for
// none: a paginated table shows the whole page it requested.
export const getTableLimit = memoize(state =>
  isPaginationActivated ? 0 : getDataRequestSize(state),
);

export const getDataRequestRange = memoize(state => {
  const page = isPaginationActivated ? getPage(state) : 0;
  const pageSize = getDataRequestSize(state);
  return [page * pageSize, (page + 1) * pageSize - 1];
});

export const getSpaceRangeSizeLimit = memoize(state => {
  const dataflow = getDataflow(state);
  return getSettingsRangeSizeLimit(dataflow.datasourceId);
});

export const getStructureQueryUrl = memoize(state => {
  const { agencyID, id, version } = getTargetDataflow(state);
  return lib_getStructureQueryUrl({
    datasource: getDatasource(state),
    identifiers: { agencyId: agencyID, code: id, version },
  });
});

const getHierarchisedDimensions = memoize(state => {
  const dimensions = getDimensions(state);
  const hierarchies = getHierarchies(state);
  return getMultiHierarchicalFilters(dimensions, hierarchies);
});

export const getDimensionsWithDataQuerySelection = memoize(
  (state, { automatedSelections } = {}) => {
    const filters = getHierarchisedDimensions(state);
    const dataquery = getDataquery(state);
    const microdataDimId = getMicrodataDimensionId(state);
    const dqParts = isNil(dataquery) ? [] : dataquery.split('.');

    return filters
      .map((_filter, index) => {
        if (isEmpty(_filter)) return _filter;

        let filter = _filter;
        if (has(_filter.id, automatedSelections)) {
          filter = {
            ..._filter,
            values: applyAutomatedSelection(_filter.values, automatedSelections[_filter.id]),
          };
        }

        const valIdsStr = dqParts[index];
        if (isNil(valIdsStr) || isEmpty(valIdsStr)) return filter;

        const valueIdsSet = new Set(valIdsStr.split('+'));
        return {
          ...filter,
          values: filter.values.map(val =>
            valueIdsSet.has(val.id) ? { ...val, isSelected: true } : val,
          ),
        };
      })
      .filter(filter => {
        // reject when ANY of these hold (R.reject(R.anyPass([...])))
        const isNotDisplayed = !filter.display;
        const isMicrodataDim = filter.id === microdataDimId;
        const isFreq = rules.isFrequencyDimension(filter);
        const hasTooFewValues = propOr([], 'values', filter).length <= 1;
        return !(isNotDisplayed || isMicrodataDim || isFreq || hasTooFewValues);
      });
  },
);

export const getFilters = memoize(
  (state, { availableConstraints = {}, automatedSelections = {} } = {}) => {
    const hasDataAvailability = getHasDataAvailability(state);
    const filters = getDimensionsWithDataQuerySelection(state, {
      automatedSelections,
    });
    const hiddenIds = getHiddenValuesAnnotation(state) ?? {};

    let dimensions = applyConstraintsToDimensions(filters, availableConstraints, 'isEnabled');
    if (hasDataAvailability) {
      dimensions = dimensions.map(dim => ({ ...dim, values: cleanDeadBranches(dim.values) }));
    }
    return dimensions.filter(filter => {
      const values = propOr([], 'values', filter);
      const hasEnoughValues = values.length > 1;
      const isNotAllHidden = hiddenIds[filter.id]
        ? !isEmpty(
            difference(
              values.map(value => value.id),
              hiddenIds[filter.id],
            ),
          )
        : true;
      return hasEnoughValues && isNotAllHidden;
    });
  },
);

export const getAvailableFrequencies = memoize(state => {
  const frequencyArtefact = getFrequencyArtefact(state);
  if (isNil(frequencyArtefact)) return {};

  const artefact = getHasDataAvailability(state)
    ? getOnlyHasDataDimensions([frequencyArtefact])[0]
    : frequencyArtefact;
  return getFrequencies(artefact);
});

export const getFrequency = memoize(state => {
  const routerPeriod = getRouterPeriod(state);
  const frequencyArtefact = getFrequencyArtefact(state);
  if (!frequencyArtefact) return;

  const dataquery = getDataquery(state) ?? '';
  const availableFrequencies = getAvailableFrequencies(state) ?? {};

  const splitDataquery = dataquery.split('.');
  const frequency = splitDataquery[frequencyArtefact.index];

  if (!frequencyArtefact.isAttribute && !isNil(frequency)) return frequency;

  const frequencyFromPeriod = getFrequencyFromSdmxPeriodSelection(routerPeriod);
  if (!isNil(frequencyFromPeriod)) return frequencyFromPeriod;

  if (frequencyArtefact.isAttribute) return Object.keys(availableFrequencies)[0];
  return;
});

export const getDatesBoundaries = memoize(state => {
  const artefact = getTimePeriodArtefact(state);
  const frequency = getFrequency(state);
  if (isNil(artefact)) return null;

  const boundaries = getHasDataAvailability(state)
    ? propOr([], 'timePeriodBoundaries', artefact)
    : sdmxPeriodBoundaries;

  return boundaries.map(getAjustedDate(frequency));
});

export const getPeriod = memoize(state => {
  const sdmxPeriod = getRouterPeriod(state);
  const frequency = getFrequency(state);
  const boundaries = getDatesBoundaries(state);

  if (isNil(sdmxPeriod)) return [undefined, undefined];
  const period = parseDateFromSdmxPeriod(frequency, sdmxPeriod);

  const source = period.length === 2 ? period : sdmxPeriod;
  return source.map(getDateInTheRange(boundaries));
});

// --- API Request Args & URL Generators ---

export const getDataRequestParams = memoize(state =>
  lib_getDataRequestParams({
    period: getRouterPeriod(state),
    lastNValue: getLastNObservations(state),
    lastNMode: getLastNMode(state),
    frequency: getFrequency(state),
    availableTimePeriod: getAvailableTimePeriod(state),
  }),
);

export const getRawDataRequestArgs = memoize(state => {
  return rawDataRequestArgsWrapper(
    getRawStructureRequestArgs(state),
    getDataquery(state),
    getDataRequestParams(state),
    getExternalReference(state),
    getDataRequestRange(state),
  );
});

export const getDataUrl = ({ agnostic }) =>
  memoize(state => getDataQueryUrl(getRawDataRequestArgs(state), { agnostic }));

export const getDataRequestArgs = memoize(state =>
  dataRequestArgsWrapper(getRawDataRequestArgs(state)),
);

export const getIsDataUrlTooLong = memoize(state => getSdmx(state)?.isDataUrlTooLong);
export const getIsApiQueryCopied = memoize(state => getSdmx(state)?.isApiQueryCopied);

export const getDataFileRequestArgs = isFull =>
  memoize(state => {
    const a = getRawDataRequestArgs(state);
    const b = getDisplay(state);
    return dataFileRequestArgsWrapper(isFull)(a, b);
  });

const formatDq = dq => (dq.match(/^[.]+$/) || isEmpty(dq) ? 'all' : dq);

export const getAvailableConstraintsArgs = ({
  dataquery = null, // from unapplied filters
  filterParams = null, // from unapplied filters
  componentId = null,
}) =>
  memoize(state => {
    const dataflow = getTargetDataflow(state);
    const datasource = getDatasource(state);
    const locale = getLocale(state);
    const timePeriodArtefact = getTimePeriodArtefact(state);
    const stateParams = getDataRequestParams(state);
    const stateDataquery = getDataquery(state);
    const params = componentId
      ? {} // FREQ and TIME_PERIOD are before default time select and must not listern to updates
      : filterParams || stateParams;
    if (!datasource || !dataflow.id) {
      return {};
    }
    const isV3 = getIsV3(datasource);
    const { startPeriod, endPeriod } = params || {};
    const timeProps =
      isV3 && !(isNil(startPeriod) && isNil(endPeriod))
        ? { c: { [timePeriodArtefact?.id]: rangeTimeSelection(startPeriod, endPeriod) } }
        : { startPeriod, endPeriod };
    const urlProps = {
      ...dataflow,
      ...timeProps,
      version: handleDataVersion(datasource)(dataflow.version),
      key: formatDq(dataquery || stateDataquery),
      endpoint: isV3 ? datasource.urlv3 : datasource.url,
      componentId: componentId || (isV3 ? '*' : undefined),
      mode: componentId ? undefined : 'available',
    };
    const queryUrlGenerator = isV3 ? availabilityQueryUrl : availabilityQueryUrlV1;
    const url = queryUrlGenerator(urlProps);
    const headers = {
      Accept: getStructureHeaderFormat(datasource),
      'Accept-Language': locale,
    };

    return { url, headers, datasourceId: datasource?.id };
  });

export const getIsCsvFileLinkHandled = memoize(state =>
  propOr(true, 'supportsCsvFile', getDatasource(state)),
);
